/**
 * Phase 140A — Stream Health Badge.
 * Phase 140B.1 — Extended with Audio Quality (derived from LiveKit
 * connectionQuality) and a Noise Reduction state row driven by the
 * broadcaster's Audio Enhancement settings.
 *
 * Broadcaster-side, lives in the operator cockpit. Reads telemetry off the
 * active LiveKit `Room` object (track stats, signal connection state, peer
 * connection RTT) plus battery (when available via the experimental
 * `navigator.getBattery()` API) and posts a heartbeat to the backend every
 * ~10 seconds so the server-side audit log has a record of stream health.
 *
 * Quality bands (single source of truth — used both for the dot color and
 * the threshold table the broadcaster sees on hover):
 *   FPS:      🟢 ≥24   🟡 ≥15   🔴 <15
 *   Bitrate:  🟢 ≥1500 🟡 ≥600  🔴 <600 (kbps)
 *   RTT:      🟢 ≤150  🟡 ≤300  🔴 >300 (ms)
 *   Battery:  🟢 ≥30%  🟡 ≥15%  🔴 <15%
 *   AudioQ:   🟢 Excellent  🟡 Good  🔴 Poor (LiveKit ConnectionQuality)
 *
 * Defensive: every browser quirk (no battery API, no stats yet, room not
 * connected) downgrades silently to a "—" cell, never throws.
 */
import { useEffect, useRef, useState } from 'react';
import { useRoomContext } from '@livekit/components-react';
import { ConnectionQuality } from 'livekit-client';
import api from '@/lib/api';
import { Wifi, Battery, Activity, Mic, ShieldCheck } from 'lucide-react';

function _band(value, gThresh, yThresh, invert = false) {
  if (value == null || Number.isNaN(value)) return 'gray';
  if (!invert) {
    if (value >= gThresh) return 'green';
    if (value >= yThresh) return 'yellow';
    return 'red';
  }
  // Lower is better (e.g. RTT).
  if (value <= gThresh) return 'green';
  if (value <= yThresh) return 'yellow';
  return 'red';
}

const DOT = {
  green: 'bg-emerald-400',
  yellow: 'bg-amber-400',
  red: 'bg-rose-500',
  gray: 'bg-white/30',
};

function Cell({ icon: Icon, label, value, band, suffix = '' }) {
  return (
    <div className="flex items-center gap-2 text-xs">
      <span className={`h-2 w-2 rounded-full ${DOT[band]}`} aria-hidden />
      <Icon className="h-3.5 w-3.5 text-white/60" />
      <span className="text-white/70 font-bold uppercase tracking-wider text-[10px]">{label}</span>
      <span className="ml-auto font-mono tabular-nums text-white" data-testid={`stream-health-${label.toLowerCase()}`}>
        {value == null ? '—' : `${value}${suffix}`}
      </span>
    </div>
  );
}

export default function StreamHealthBadge({ streamId, noiseSuppressionOn = true }) {
  const room = useRoomContext();
  const [stats, setStats] = useState({ fps: null, bitrate: null, rtt: null, battery: null, audioOk: null, audioQuality: null });
  const lastHeartbeatRef = useRef(0);

  useEffect(() => {
    if (!room) return undefined;
    let cancelled = false;

    async function probeBattery() {
      try {
        if (typeof navigator !== 'undefined' && typeof navigator.getBattery === 'function') {
          const b = await navigator.getBattery();
          return b?.level ?? null;
        }
      } catch (_e) {
        // not available — fine
      }
      return null;
    }

    async function sample() {
      if (cancelled || !room) return;
      let fps = null;
      let bitrate = null;
      let rtt = null;
      let audioOk = null;
      let audioQuality = null;
      try {
        const pubs = Array.from(room.localParticipant?.trackPublications?.values?.() || []);
        for (const p of pubs) {
          const track = p?.track;
          if (!track) continue;
          if (track.kind === 'video') {
            // Best-effort: LiveKit's track stats expose framesPerSecond when
            // available; fall back to the publication-level metric if not.
            const tStats = await track.getRTCStatsReport?.();
            if (tStats) {
              for (const stat of tStats.values()) {
                if (stat.type === 'outbound-rtp' && stat.mediaType === 'video') {
                  if (typeof stat.framesPerSecond === 'number') fps = stat.framesPerSecond;
                }
              }
            }
            bitrate = Math.round(((track.currentBitrate || 0) / 1000) || 0) || bitrate;
          } else if (track.kind === 'audio') {
            audioOk = !p.isMuted;
          }
        }
        // Signal connection quality → coarse RTT band when no precise stat exists.
        const cq = room.localParticipant?.connectionQuality;
        if (cq === ConnectionQuality.Excellent) { rtt = rtt ?? 60; audioQuality = 'Excellent'; }
        else if (cq === ConnectionQuality.Good) { rtt = rtt ?? 140; audioQuality = 'Good'; }
        else if (cq === ConnectionQuality.Poor) { rtt = rtt ?? 320; audioQuality = 'Poor'; }
        // If mic is muted, drop audio quality to null — there's no audio
        // to assess.
        if (audioOk === false) audioQuality = null;
      } catch (_e) {
        // any stat-collection failure → keep nulls
      }

      const battery = await probeBattery();

      const fresh = {
        fps: fps != null ? Math.round(fps) : null,
        bitrate,
        rtt,
        battery: battery != null ? Math.round(battery * 100) : null,
        audioOk,
        audioQuality,
      };
      if (cancelled) return;
      setStats(fresh);

      // Heartbeat the backend at most every 10s.
      const now = Date.now();
      if (streamId && now - lastHeartbeatRef.current > 10_000) {
        lastHeartbeatRef.current = now;
        try {
          await api.post(`/livex/streams/${streamId}/health`, {
            fps: fresh.fps,
            bitrate_kbps: fresh.bitrate,
            rtt_ms: fresh.rtt,
            battery: battery,
            audio_ok: fresh.audioOk,
            audio_quality: fresh.audioQuality,
            noise_suppression: !!noiseSuppressionOn,
          });
        } catch (_e) { /* best-effort */ }
      }
    }

    sample();
    const id = setInterval(sample, 4000);
    return () => { cancelled = true; clearInterval(id); };
  }, [room, streamId, noiseSuppressionOn]);

  const fpsBand = _band(stats.fps, 24, 15);
  const bitBand = _band(stats.bitrate, 1500, 600);
  const rttBand = _band(stats.rtt, 150, 300, /*invert*/ true);
  const battBand = _band(stats.battery, 30, 15);
  const audioBand = stats.audioOk == null ? 'gray' : (stats.audioOk ? 'green' : 'red');
  const audioQBand = stats.audioQuality === 'Excellent' ? 'green'
    : stats.audioQuality === 'Good' ? 'yellow'
    : stats.audioQuality === 'Poor' ? 'red'
    : 'gray';
  const nrBand = noiseSuppressionOn ? 'green' : 'gray';

  return (
    <div className="p-4 bg-[#07111F] border-2 border-[#22314F] rounded-xl space-y-2" data-testid="stream-health-badge">
      <div className="livex-label" style={{ marginBottom: 0 }}>Stream Health</div>
      <Cell icon={Activity} label="FPS" value={stats.fps} band={fpsBand} />
      <Cell icon={Wifi} label="Bitrate" value={stats.bitrate} band={bitBand} suffix=" kbps" />
      <Cell icon={Wifi} label="RTT" value={stats.rtt} band={rttBand} suffix=" ms" />
      <Cell icon={Battery} label="Battery" value={stats.battery} band={battBand} suffix="%" />
      <Cell icon={Mic} label="Mic" value={stats.audioOk == null ? null : (stats.audioOk ? 'On' : 'Muted')} band={audioBand} />
      <Cell icon={Mic} label="Audio Quality" value={stats.audioQuality} band={audioQBand} />
      <Cell icon={ShieldCheck} label="Noise Reduction" value={noiseSuppressionOn ? 'On' : 'Off'} band={nrBand} />
    </div>
  );
}
